import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["item", "parentPlaylist", "parentTrack", "onShare"],
  _excluded2 = ["renderSubmenuRow", "item", "parentPlaylist", "parentTrack"];
import I18n from 'I18n';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import PortalIdParser from 'PortalIdParser';
import UsageTracker from 'academy-common-lib/utils/UsageTracker';
import { useUserInfo } from '../../accounts/accountsQueries';
import { DuplicateIcon } from 'foundations-assets/icons/components/DuplicateIcon';
import { SocialFacebookIcon } from 'foundations-assets/icons/components/SocialFacebookIcon';
import { SocialLinkedinIcon } from 'foundations-assets/icons/components/SocialLinkedinIcon';
import { SocialWhatsappIcon } from 'foundations-assets/icons/components/SocialWhatsappIcon';
import { SocialXIcon } from 'foundations-assets/icons/components/SocialXIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import { getShareLinks, getProxyUrl, getOverviewModalParams } from 'academy-common-lib/utils/url';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { generatePath } from 'quartz-routing/react-router';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Inlined from LCU RoutingUtils: builds a referral link with ruid param and portal context
const buildReferralLink = (to, ruid, allowedParams = ['language']) => {
  const searchParams = new URLSearchParams(window.location.search);
  [...searchParams.keys()].forEach(param => {
    if (!allowedParams.includes(param)) {
      searchParams.delete(param);
    }
  });
  searchParams.append('ruid', ruid.toString());
  const toPath = to && to.length && to.startsWith('/') ? to.substring(1, to.length) : to;
  const urlObject = new URL(`https://${window.location.hostname}/academy/${PortalIdParser.getPortalIdFromPath()}/${toPath}`);
  const searchBuilder = new URLSearchParams(urlObject.search);
  searchParams.forEach((value, key) => searchBuilder.set(key, value));
  urlObject.search = searchBuilder.toString();
  return urlObject.toString();
};

// Inlined from LCU shortContentRouting: returns simple path for short content
const getShortContentUrl = ({
  shortId
}) => {
  const SHORT_CONTENT_URL_FRAGMENT = '/shortvideo/:shortId';
  const pathname = generatePath(SHORT_CONTENT_URL_FRAGMENT, {
    shortId
  });
  return {
    fullUrl: pathname
  };
};

// Inlined from LCU RoutingUtils: builds share URL for tracks
const getTrackShareUrl = (track, userId, trackId, language) => {
  if (track && track.visibleOnLoggedOutPage && track.pagePath) {
    return getProxyUrl(`courses/${track.pagePath}`, language === null || language === void 0 ? void 0 : language.toLowerCase());
  }
  return buildReferralLink(`/?${new URLSearchParams(getOverviewModalParams({
    id: trackId,
    type: 'TRACK',
    language
  }))}`, userId);
};

// Inlined from LCU RoutingUtils: builds share URL for lessons
const getLessonShareUrl = (lesson, userId, lessonId, language) => {
  if (lesson && lesson.visibleOnLoggedOutPage && lesson.pagePath) {
    return getProxyUrl(`lessons/${lesson.pagePath}`, language === null || language === void 0 ? void 0 : language.toLowerCase());
  }
  return buildReferralLink(`/?${new URLSearchParams(getOverviewModalParams({
    id: lessonId,
    type: 'LESSON',
    language
  }))}`, userId);
};
const getPlaylistUrl = playlistId => `/saved-items/playlists/${playlistId}`;
const getReferalLink = (item, userId) => {
  const {
    id,
    type,
    language
  } = item;
  switch (type) {
    case 'PLAYLIST':
    case 'LEARNING_PATH':
      return buildReferralLink(getPlaylistUrl(item.id), userId);
    case 'LESSON':
      return getLessonShareUrl(item, userId, id, language);
    case 'TRACK':
      return getTrackShareUrl(item, userId, id, language);
    case 'SHORT_FORM_CONTENT':
      return buildReferralLink(getShortContentUrl({
        shortId: item.id
      }).fullUrl, userId);
    default:
      return null;
  }
};
const getShareLinksForLearningContent = (item, userId) => {
  const link = getReferalLink(item, userId);
  const isShareable = item.visibility !== 'IN_TRACK_ONLY';
  return link && isShareable ? Object.assign({
    link
  }, getShareLinks(link, `${item.name}: `)) : null;
};
const SocialShareListOption = ({
  onClick,
  href,
  renderShareRow = button => button,
  rowSpaceStyleProps,
  children
}) => renderShareRow(href ? /*#__PURE__*/_jsx(Link, Object.assign({
  display: "block",
  href: href,
  onClick: onClick,
  target: "_blank",
  use: "unstyled"
}, rowSpaceStyleProps, {
  children: children
})) : children);
const SocialShareIcon = ({
  icon
}) => {
  return /*#__PURE__*/_jsx(IconHolder, {
    color: "text-core-default",
    icon: icon,
    padding: "0",
    paddingInlineEnd: "200",
    size: "300"
  });
};
const getSocialShareListOptions = (_ref, {
  link,
  linkForFacebook,
  linkForLinkedIn,
  linkForWhatsapp,
  linkForX
}) => {
  let {
      item,
      parentPlaylist,
      parentTrack,
      onShare
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const handleOnClick = (shareType, url) => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'social-share',
      to: shareType,
      contentId: item.id,
      contentType: item.type,
      playlistId: parentPlaylist === null || parentPlaylist === void 0 ? void 0 : parentPlaylist.id,
      trackId: parentTrack === null || parentTrack === void 0 ? void 0 : parentTrack.id
    });
    if (onShare) onShare(shareType, url);
  };
  return [/*#__PURE__*/_jsx(SocialShareListOption, Object.assign({}, props, {
    children: /*#__PURE__*/_jsx(CopyButton, Object.assign({
      onClick: () => {
        FloatingAlertStore.addAlert({
          type: 'success',
          message: I18n.text('academyCommon.shareThis.notification')
        });
        handleOnClick('link');
      },
      padding: "0",
      responsive: true,
      tooltipPlacement: "right",
      use: "unstyled",
      value: link
    }, props.rowSpaceStyleProps, {
      children: /*#__PURE__*/_jsxs(View, {
        color: "text-core-default",
        inlineSize: "100%",
        textAlign: "start",
        children: [/*#__PURE__*/_jsx(SocialShareIcon, {
          icon: DuplicateIcon
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyCommon.socialShare.copyLink"
        })]
      })
    }))
  }), "copy-link"), /*#__PURE__*/_jsxs(SocialShareListOption, Object.assign({
    href: linkForX,
    onClick: () => handleOnClick('x', linkForX)
  }, props, {
    children: [/*#__PURE__*/_jsx(SocialShareIcon, {
      icon: SocialXIcon
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.socialShare.shareX"
    })]
  }), "share-x"), /*#__PURE__*/_jsxs(SocialShareListOption, Object.assign({
    href: linkForFacebook,
    onClick: () => handleOnClick('facebook', linkForFacebook)
  }, props, {
    children: [/*#__PURE__*/_jsx(SocialShareIcon, {
      icon: SocialFacebookIcon
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.socialShare.shareFacebook"
    })]
  }), "share-facebook"), /*#__PURE__*/_jsxs(SocialShareListOption, Object.assign({
    href: linkForLinkedIn,
    onClick: () => handleOnClick('linkedin', linkForLinkedIn)
  }, props, {
    children: [/*#__PURE__*/_jsx(SocialShareIcon, {
      icon: SocialLinkedinIcon
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.socialShare.shareLinkedIn"
    })]
  }), "share-linkedin"), /*#__PURE__*/_jsxs(SocialShareListOption, Object.assign({
    href: linkForWhatsapp,
    onClick: () => handleOnClick('whatsapp', linkForWhatsapp)
  }, props, {
    children: [/*#__PURE__*/_jsx(SocialShareIcon, {
      icon: SocialWhatsappIcon
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.socialShare.shareWhatsApp"
    })]
  }), "share-whatsapp")];
};
const SocialShareListSubmenu = ({
  item,
  renderSubmenuRow = (shareMessage, itemName) => /*#__PURE__*/_jsxs(View, {
    children: [/*#__PURE__*/_jsx(Text, {
      color: "text-core-default",
      typography: "heading-50",
      children: shareMessage
    }), /*#__PURE__*/_jsx(Text, {
      color: "text-tertiary-default",
      children: itemName
    })]
  })
}) => {
  const isShareable = item.visibility !== 'IN_TRACK_ONLY';
  if (!isShareable) return null;
  return renderSubmenuRow( /*#__PURE__*/_jsx(FormattedMessage, {
    message: `academyCommon.socialShare.submenu.${item.type}`
  }), item.name);
};
export { SocialShareListOption };
export const useSocialShareList = _ref2 => {
  let {
      renderSubmenuRow,
      item,
      parentPlaylist,
      parentTrack
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    userInfo
  } = useUserInfo();
  const userId = Number(userInfo && userInfo.user.id);
  const itemShareLinks = getShareLinksForLearningContent(item, userId);
  const playlistShareLinks = parentPlaylist && getShareLinksForLearningContent(parentPlaylist, userId);
  const trackShareLinks = parentTrack && getShareLinksForLearningContent(parentTrack, userId);
  if (playlistShareLinks || trackShareLinks) {
    // shared item is contained in a playlist or a track, so we need to
    // ask first which item to share (playlist, track or the lesson/short)
    const list = [];
    if (playlistShareLinks) {
      list.push({
        label: /*#__PURE__*/_jsx(SocialShareListSubmenu, {
          item: parentPlaylist,
          renderSubmenuRow: renderSubmenuRow
        }, "share-playlist-submenu"),
        options: getSocialShareListOptions(Object.assign({
          item: parentPlaylist
        }, props), playlistShareLinks)
      });
    }
    if (trackShareLinks) {
      list.push({
        label: /*#__PURE__*/_jsx(SocialShareListSubmenu, {
          item: parentTrack,
          renderSubmenuRow: renderSubmenuRow
        }, "share-track-submenu"),
        options: getSocialShareListOptions(Object.assign({
          item: parentTrack
        }, props), trackShareLinks)
      });
    }
    if (itemShareLinks) {
      list.push({
        label: /*#__PURE__*/_jsx(SocialShareListSubmenu, {
          item: item,
          renderSubmenuRow: renderSubmenuRow
        }, "share-item-submenu"),
        options: getSocialShareListOptions(Object.assign({
          item
        }, props), itemShareLinks)
      });
    }
    return list;
  }
  if (itemShareLinks) {
    return getSocialShareListOptions(Object.assign({
      item
    }, props), itemShareLinks);
  }
  return [];
};